import React, { useState } from "react";
import { MobileContainerPadding } from "./index.style";
import { PageTitle } from "domain/feature/hub/pageTitle/styles";
import { useTranslation } from "react-i18next";
import { useSmartNavigateLegacy as useNavigate } from "components/SmartLink";
import { Button, Col, Row } from "react-bootstrap";
import { NoContent } from "components/NoContent";
import { Loading } from "components/loading";
import NoContentImg from "assets/images/defaults/school.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FilterOrderBar } from "sharedKernel/styledComponents/filterComponents/style";
import { Filter } from "components/Filter";
import { FilterOrderByProps, OrderBy } from "components/OrderBy";
import { Track } from "domain/track/trackContainer";
import { AdmTrackCardList } from "domain/track/AdmTrackCardList";
import { showConfirmationPopUp } from "components/popup/confirmationModal";
import { useTracksList } from "./hooks/useTracksList";
import { trackService } from "data/services/trackService";

export const TracksPage = ({ ...props }): JSX.Element => {
  const { t } = useTranslation();
  const orderByOptions = [
    {
      value: "updated_at desc",
      label: t("Mais recente"),
    },
    {
      value: "title asc",
      label: t("Ordem alfabética"),
    },
  ];

  const navigate = useNavigate();
  const [selectedOrderBy, setSelectedOrderBy] = useState(orderByOptions[0]);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);

  const labels = {
    noContent: t("Você ainda não possui nenhuma trilha criada"),
    title: t("Trilhas"),
    newTrack: t("Criar Trilha"),
  };


  const apiSort = React.useMemo(() => {
    const value = (selectedOrderBy?.value || '').toLowerCase();
    if (value.includes('updated_at')) return 'CreatedAt desc';
    if (value.includes('title') && value.includes('asc')) return 'Title asc';
    return 'CreatedAt desc';
  }, [selectedOrderBy]);

  const apiPage = Math.max(0, (currentPage || 1) - 1);

  const { data, isLoading, error, refetch } = useTracksList(apiPage, limit, apiSort, undefined, currentSearchTerm || undefined);


  const handleEdit = (id: Track["id"]) => {
    navigate(`/hub/tracks/${id}/edit`);
  };

  const handleDelete = async (id: Track["id"]) => {
    const track = data.rows.find(t => t.id === id);
    const trackName = track?.title || "esta trilha";

    showConfirmationPopUp({
      title: "Excluir Trilha",
      message: `Você deseja realmente excluir a trilha "${trackName}"?`,
      onConfirm: async () => {
        try {
          await trackService.deleteTrack(String(id));
          refetch();
        } catch (error) {
          console.error("Erro ao excluir trilha:", error);
        }
      }
    });
  };

  const handleCardClick = (track: Track) => {
    navigate(`/hub/tracks/${track.id}`, {
      state: { track }
    });
  };

  const onOrderBy = (selectedOrderBy: FilterOrderByProps): void => {
    setSelectedOrderBy(selectedOrderBy);
    refetch();
  };

  const onSearch = (searchTerm: string): void => {
    setCurrentSearchTerm(searchTerm);
    refetch();
  };



  const FilterOrderBarElement = (): JSX.Element => {
    return (
      <FilterOrderBar>
        <Filter currentSearchTerm={currentSearchTerm} btnClick={onSearch} />
        <OrderBy
          selectedValue={selectedOrderBy}
          onSelect={onOrderBy}
          options={orderByOptions}
        />
      </FilterOrderBar>
    );
  };

  const PageHeader = (): JSX.Element => {
    const buttonProps = {
      variant: "primary" as const,
      style: { letterSpacing: "1.5px", whiteSpace: "nowrap" as const },
      onClick: () => navigate("/hub/tracks/create"),
    };
    return (
      <Row className="align-items-center mb-md-4">
        <Col>
          <PageTitle>{labels.title}</PageTitle>
          <Button {...buttonProps} className="d-md-none fw-bold fs-6 px-3" style={{ ...buttonProps.style, marginBottom: "16px" }}>
            <FontAwesomeIcon icon={["fas", "plus"]} className="me-2" />
            {labels.newTrack}
          </Button>
        </Col>
        <Col md="auto" className="d-none d-md-block text-end p-0">
          <Button {...buttonProps} className="fw-bold fs-6 px-4">
            <FontAwesomeIcon icon={["fas", "plus"]} className="me-2" />
            {labels.newTrack}
          </Button>
        </Col>
      </Row>
    );
  };

  if (isLoading) {
    return (
      <>
        <PageHeader />
        <Loading />
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader />
        <div className="text-center py-5">
          <FontAwesomeIcon icon={["fas", "exclamation-triangle"]} className="me-2 text-warning" />
          <p className="text-danger">{error}</p>
          <Button variant="outline-primary" onClick={refetch}>
            Tentar novamente
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <MobileContainerPadding />
      <PageHeader />
      {data && data.rows && data.rows.length > 0 && (
        <FilterOrderBarElement />
      )}


      {!data || !data.rows || data.rows.length === 0 ? (
        <NoContent text={labels.noContent} image={NoContentImg} />
      ) : (
        <AdmTrackCardList
          tracks={data.rows}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onCardClick={handleCardClick}
        />
      )}

    </>
  );
};
